/*
 * Layout  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent, isNumber } from "@adapptio/lib-shared";
import { ARROW_POSITION } from "../Toolbar/Toolbar";
import {
	ILayoutConfig,
	ILayoutElementConfig_Column,
	ILayoutElementConfig_Panel,
	ILayoutElementConfig_Row,
	ILayoutElementConfig_Splitter,
	ILayoutElementConfig_Tabs,
	ILayoutElementConfigBase,
	ILayoutElementConfigExpandable,
	ILayoutElementStateData_Column,
	ILayoutElementStateData_Panel,
	ILayoutElementStateData_Row,
	ILayoutElementStateData_Splitter,
	ILayoutElementStateData_Tabs,
	ILayoutElementStateDataBase,
	ILayoutElementStateDataExpandable,
	ILayoutStateData,
	LAYOUT_TYPE,
	TLayoutElementConfig,
	TLayoutElementStateData,
	LAYOUT_TABS_STYLE
} from "./LayoutTypes";
import { isBoolean } from "@adapptio/lib-shared";
import { FloatingPanelManager } from "../FloatingPanel";

abstract class LayoutStateElement<
	TConfig extends ILayoutElementConfigBase,
	TStateData extends ILayoutElementStateDataBase
> {
	/** Element ID */
	public id: string;

	/** Layout type */
	public type: LAYOUT_TYPE;

	/** Emitted when state change */
	public onChange = createEventEmitter<void>();

	/**
	 * State constructor
	 *
	 * @param config Element config
	 */
	public constructor(protected config: TConfig) {
		this.id = config.id;
		this.type = config.type;
	}

	public getStateData(): TStateData {
		return {
			id: this.id
		} as TStateData;
	}

	public getById<TResult extends TLayoutState = TLayoutState>(id: string): TResult {
		return this.id === id ? (this as unknown as TResult) : null;
	}
}

export abstract class LayoutStateElementExpandable<
	TConfig extends ILayoutElementConfigExpandable,
	TStateData extends ILayoutElementStateDataExpandable
> extends LayoutStateElement<TConfig, TStateData> {
	public isExpandable: boolean;
	public isHideable: boolean;
	public size: number | string;
	public isExpanded: boolean;
	public isHidden: boolean;
	public renderWhenCollapsed: boolean;
	public renderWhenHidden: boolean;

	public constructor(config: TConfig, stateData?: TStateData) {
		super(config);

		this.size =
			stateData?.size !== undefined ? stateData.size : config.size !== undefined ? config.size : 100;

		this.isExpandable = config.isExpandable || false;

		this.isExpanded =
			stateData?.isExpanded !== undefined
				? stateData.isExpanded
				: config.defaultExpanded !== undefined
				? config.defaultExpanded
				: true;

		this.isHideable = config.isHideable || false;

		this.isHidden = isBoolean(stateData?.isHidden)
			? stateData.isHidden
			: isBoolean(config?.defaultHidden)
			? config.defaultHidden
			: false;

		this.renderWhenHidden = config?.renderWhenHidden || false;
	}

	public expand(): void {
		this.isExpanded = true;
		emitEvent(this.onChange);
	}

	public collapse(): void {
		this.isExpanded = false;
		emitEvent(this.onChange);
	}

	public toggleExpanded = (): void => {
		this.isExpanded = !this.isExpanded;
		emitEvent(this.onChange);
	};

	public hide(): void {
		this.isHidden = true;
		emitEvent(this.onChange);
	}

	public show(): void {
		this.isHidden = false;
		emitEvent(this.onChange);
	}

	public toggleHidden(): void {
		this.isHidden = !this.isHidden;
		emitEvent(this.onChange);
	}

	public setSize(size: number | string): void {
		this.size = size;
		emitEvent(this.onChange);
	}

	public getStateData(): TStateData {
		return {
			...super.getStateData(),
			isExpanded: this.isExpanded,
			size: this.size,
			isHidden: this.isHidden
		} as TStateData;
	}
}

export class LayoutState_Row extends LayoutStateElementExpandable<
	ILayoutElementConfig_Row,
	ILayoutElementStateData_Row
> {
	public children: TLayoutState[];

	public constructor(config: ILayoutElementConfig_Row, stateData?: ILayoutElementStateData_Row) {
		super(config, stateData);
		this.children = createChildren(config.children || [], stateData?.children || []);
	}

	public getStateData(): ILayoutElementStateData_Row {
		return {
			...super.getStateData(),
			children: this.children.map((child) => child.getStateData())
		};
	}

	public getById<TResult extends TLayoutState = TLayoutState>(id: string): TResult {
		const self = super.getById<TResult>(id);

		if (self) {
			return self;
		}

		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i].getById<TResult>(id);

			if (child) {
				return child;
			}
		}

		return null;
	}
}

export class LayoutState_Column extends LayoutStateElementExpandable<
	ILayoutElementConfig_Column,
	ILayoutElementStateData_Column
> {
	public children: TLayoutState[];

	public constructor(config: ILayoutElementConfig_Column, stateData?: ILayoutElementStateData_Column) {
		super(config, stateData);
		this.children = createChildren(config.children || [], stateData?.children || []);
	}

	public getStateData(): ILayoutElementStateData_Column {
		return {
			...super.getStateData(),
			children: this.children.map((child) => child.getStateData())
		};
	}

	public getById<TResult extends TLayoutState = TLayoutState>(id: string): TResult {
		const self = super.getById<TResult>(id);

		if (self) {
			return self;
		}

		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i].getById<TResult>(id);

			if (child) {
				return child;
			}
		}

		return null;
	}
}

export class LayoutState_Panel extends LayoutStateElementExpandable<
	ILayoutElementConfig_Panel,
	ILayoutElementStateData_Panel
> {
	public arrowPosition: ARROW_POSITION;
	public toolbarComponent?: string;
	public secondaryToolbarComponent?: string;
	public contentComponent: string;

	public children: TLayoutState[];

	public constructor(config: ILayoutElementConfig_Panel, stateData?: ILayoutElementStateData_Panel) {
		super(config, stateData);

		this.arrowPosition = config.arrowPosition || ARROW_POSITION.LEFT;
		this.toolbarComponent = config.toolbarComponent || null;
		this.secondaryToolbarComponent = config.secondaryToolbarComponent || null;
		this.contentComponent = config.contentComponent || null;
		this.children = createChildren(config.children || [], stateData?.children || []);
	}

	public getStateData(): ILayoutElementStateData_Column {
		return {
			...super.getStateData(),
			children: this.children.map((child) => child.getStateData())
		};
	}

	public getById<TResult extends TLayoutState = TLayoutState>(id: string): TResult {
		const self = super.getById<TResult>(id);

		if (self) {
			return self;
		}

		for (let i = 0; i < this.children.length; i++) {
			const child = this.children[i].getById<TResult>(id);

			if (child) {
				return child;
			}
		}

		return null;
	}
}

export class LayoutState_Tabs extends LayoutStateElementExpandable<
	ILayoutElementConfig_Tabs,
	ILayoutElementStateData_Tabs
> {
	public size: number | string;
	public tabs: Array<{
		id: string;
		label?: string;
		icon?: string;
		tooltip?: string;
		contentComponent: string;
		renderWhenHidden?: boolean;
		additionalTabs?: string;
	}>;

	public currentTabIndex: number;
	public arrowPosition: ARROW_POSITION;
	public styles: LAYOUT_TABS_STYLE;

	public constructor(config: ILayoutElementConfig_Tabs, stateData?: ILayoutElementStateData_Tabs) {
		super(config);

		this.size =
			stateData?.size !== undefined ? stateData.size : config.size !== undefined ? config.size : null;

		this.tabs = config.tabs;
		this.arrowPosition = config.arrowPosition;
		this.styles = config.styles || LAYOUT_TABS_STYLE.EXPAND;

		this.currentTabIndex =
			stateData?.currentTabIndex !== undefined
				? stateData.currentTabIndex
				: config.defaultTabIndex !== undefined
				? config.defaultTabIndex
				: 0;

		if (this.currentTabIndex < 0 || this.currentTabIndex >= this.tabs.length) {
			this.currentTabIndex = 0;
		}
	}

	public getStateData(): ILayoutElementStateData_Tabs {
		return {
			...super.getStateData(),
			currentTabIndex: this.currentTabIndex
		};
	}

	public setSize(size: number | string): void {
		this.size = size;
		emitEvent(this.onChange);
	}

	public selectTab(tabIndexOrId: number | string): void {
		const tabIndex = isNumber(tabIndexOrId)
			? tabIndexOrId
			: this.tabs.findIndex((tab) => tab.id === tabIndexOrId);

		if (tabIndex < 0 || tabIndex >= this.tabs.length) {
			throw new Error(`Tab with index '${tabIndex}' does not exist.`);
		}

		this.currentTabIndex = tabIndex;
		emitEvent(this.onChange);
	}
}

export class LayoutState_Splitter extends LayoutStateElement<
	ILayoutElementConfig_Splitter,
	ILayoutElementStateData_Splitter
> {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(config: ILayoutElementConfig_Splitter, _stateData: ILayoutElementStateData_Splitter) {
		super(config);
	}

	public getStateData(): ILayoutElementStateData_Splitter {
		return {
			...super.getStateData()
		};
	}
}

function createChildren(
	config: TLayoutElementConfig[],
	stateData?: TLayoutElementStateData[]
): TLayoutState[] {
	return config.map((child, index) => {
		switch (child.type) {
			case LAYOUT_TYPE.ROW:
				return new LayoutState_Row(child, stateData[index] as ILayoutElementStateData_Row);
			case LAYOUT_TYPE.COLUMN:
				return new LayoutState_Column(child, stateData[index] as ILayoutElementStateData_Column);
			case LAYOUT_TYPE.PANEL:
				return new LayoutState_Panel(child, stateData[index] as ILayoutElementStateData_Panel);
			case LAYOUT_TYPE.TABS:
				return new LayoutState_Tabs(child, stateData[index] as ILayoutElementStateData_Tabs);
			case LAYOUT_TYPE.SPLITTER:
				return new LayoutState_Splitter(child, stateData[index] as ILayoutElementStateData_Splitter);
			default:
				throw new Error(`Unknown layout type '${(child as TLayoutElementConfig).type}'.`);
		}
	});
}

export type TLayoutState =
	| LayoutState_Row
	| LayoutState_Column
	| LayoutState_Panel
	| LayoutState_Tabs
	| LayoutState_Splitter;

export class LayoutState {
	public root: TLayoutState;
	private floatingPanelManager = new FloatingPanelManager();

	public constructor(config: ILayoutConfig, stateData?: ILayoutStateData) {
		this.root = createChildren([ config.root ], [ stateData?.root ])[0];
	}

	public getStateData(): ILayoutStateData {
		return {
			root: this.root.getStateData()
		};
	}

	public getById<TResult extends TLayoutState = TLayoutState>(id: string): TResult {
		return this.root.getById<TResult>(id);
	}

	public getFloatingPanelManager(): FloatingPanelManager {
		return this.floatingPanelManager;
	}

	public dispose(): void {
		this.floatingPanelManager.dispose();
	}
}
